/**
 * Objects and arrays.
 */

import { isArray, isPlainObject, isNumeric } from './js';

/**
 * Ensure a value is an array.
 *
 * @param {*} val - Value to convert.
 * @returns {Array.<*>}
 * @example
 *
 * toArray(3);
 * // => [3]
 *
 * toArray([1, 2, 3]);
 * // => [1, 2, 3]
 */
export function toArray(val) {
  return Array.isArray(val) ? val : [val];
}

/**
 * Filter an object.
 *
 * @param {object} obj - The object to filter.
 * @param {Function} predicate - The function invoked on each value, return
 *   true to keep the item, false to discard it. Is passed the current value,
 *   current key and the object itself.
 * @returns {object} The new filtered object.
 */
export function filterObject(obj, predicate) {
  const result = {};
  Object.entries(obj).forEach(([key, val]) => {
    if (predicate(val, key, obj)) {
      result[key] = val;
    }
  });
  return result;
}

/**
 * Conditionally assign properties to a new object.
 *
 * @param {Function} condition - Condition that must return true for the
 *   property to be included in the resulting object. Called for each property
 *   with (value, key, object).
 * @param {...object} objects - Objects to assign.
 * @returns {object} The new object.
 */
export function conditionalAssign(condition, ...objects) {
  const filtered = objects.map((obj) =>
    Object.keys(obj).reduce((result, key) => {
      if (!condition(obj[key], key, obj)) {
        return result;
      }
      return Object.assign(result, { [key]: obj[key] });
    }, {}),
  );
  return Object.assign({}, ...filtered);
}

/**
 * Create an object with the same values as `obj` and keys generated by running
 * each property through `iteratee`.
 *
 * @param {object} obj - The object to iterate over.
 * @param {Function} iteratee - The function invoked per iteration. Is passed
 *   the current value, current key and the object itself.
 * @returns {object} The new mapped object.
 * @example
 *
 * mapKeys({ 'a': 1, 'b': 2 }, (val, key) => `${key}${val}`);
 * // => { 'a1': 1, 'b2': 2 }
 */
export function mapKeys(obj, iteratee) {
  const result = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    result[iteratee(value, key, obj)] = value;
  });
  return result;
}

/**
 * Create an object with the same keys as `obj` and values generated by running
 * each property through `iteratee`.
 *
 * @param {object} obj - The object to iterate over.
 * @param {Function} iteratee - The function invoked per iteration. Is passed
 *   the current value, current key and the object itself.
 * @returns {object} The new mapped object.
 * @example
 *
 * mapValues({ 'a': 1, 'b': 2 }, (val) => val * 2);
 * // => { 'a': 2, 'b': 4 }
 */
export function mapValues(obj, iteratee) {
  const result = {};
  Object.keys(obj).forEach((key) => {
    result[key] = iteratee(obj[key], key, obj);
  });
  return result;
}

/**
 * Recursivly map over a collection.
 *
 * @param {object|Array} collection - The collection to iterate over.
 * @param {Function} iteratee - The function invoked on each value.
 * @returns {object} The new mapped object.
 */
export function mapDeep(collection, iteratee) {
  const mapper = (val, key) =>
    isPlainObject(val) || isArray(val)
      ? mapDeep(val, iteratee)
      : iteratee(val, key);

  if (isArray(collection)) {
    return collection.map(mapper);
  }

  if (isPlainObject(collection)) {
    return mapValues(collection, mapper);
  }

  return collection;
}

/* ========================================================================= *\
    Immutable array modification
\* ========================================================================= */

/**
 * Shallow copy an array.
 *
 * @param {Array.<*>} arr - The array.
 * @returns {Array.<*>} The copy.
 */
export function copyArray(arr) {
  return [...arr];
}

/**
 * Shallow copy an array and add an item to the end of it.
 *
 * @see Array.prototype.push
 * @param {Array.<*>} arr - The array.
 * @param {*} newEntry - The item to add.
 * @returns {Array.<*>} The new array.
 */
export function immutablePush(arr, newEntry) {
  return [...arr, newEntry];
}

/**
 * Shallow copy an array and add an item to the beginning of it.
 *
 * @see Array.prototype.unshift
 * @param {Array.<*>} arr - The array.
 * @param {*} newEntry - The item to add.
 * @returns {Array.<*>} The new array.
 */
export function immutableUnshift(arr, newEntry) {
  return [newEntry, ...arr];
}

/**
 * Shallow copy an array and sort it.
 *
 * @see Array.prototype.sort
 * @param {Array.<*>} arr - The array.
 * @param {Function} compareFunction - Function that defines the sort order.
 * @returns {Array.<*>} The sorted array.
 */
export function immutableSort(arr, compareFunction) {
  return copyArray(arr).sort(compareFunction);
}

/**
 * Shallow copy an array and reverse it.
 *
 * @see Array.prototype.reverse
 * @param {Array.<*>} arr - The array.
 * @returns {Array.<*>} The reversed array.
 */
export function immutableReverse(arr) {
  return copyArray(arr).reverse();
}

/**
 * Shallow copy an array and remove or replace existing items from and/or add
 * new items to it.
 *
 * @see Array.prototype.splice
 * @param {Array.<*>} arr - The array.
 * @param {number} [start] - The index at which to start changing the array.
 * @param {number} [deleteCount] - The number of items to remove, beginning
 *   from `start`.
 * @param {...*} [items] - Elements to add, beginning from `start`.
 * @returns {Array.<*>} The new array.
 */
export function immutableSplice(arr, start, deleteCount = 0, ...items) {
  // Match native
  if (start === undefined) {
    return arr;
  }
  if (!isNumeric(start)) {
    throw new TypeError('Start index should be a number');
  }
  // NaN > 0 evaluates to false, so a non-numeric deleteCount like `null`
  // will work.
  const clearToEnd = !Number(deleteCount) > 0 && !items.length;
  return [
    ...arr.slice(0, Number(start)),
    ...items,
    ...arr.slice(start + (clearToEnd ? arr.length : deleteCount)),
  ];
}

/**
 * Shallow copy an array and delete an item from it.
 *
 * @param {Array.<*>} arr - The array.
 * @param {number} index - Which index to delete.
 * @returns {Array.<*>} The new array.
 */
export function immutableDelete(arr, index) {
  return arr.slice(0, index).concat(arr.slice(index + 1));
}
